<script setup lang="ts">
import type { SwitchEmit, SwitchProps } from '/#/components/base/form/switch'
import type { IconName } from '/#/components/base/icon/icon'
import type { ComputedRef, WritableComputedRef } from 'vue'

const props = withDefaults(defineProps<SwitchProps>(), {
  modelValue: false,
})

const emit = defineEmits<SwitchEmit>()

const modelValue: WritableComputedRef<boolean> = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value),
})

/**
 * @name appearance
 * @type {ComputedRef<Array<string|boolean>}
 * @description Applies styles for switch wrapper.
 */
const appearance: ComputedRef<Array<string | boolean>> = computed(() => [
  modelValue.value && 'checked',
  props.disabled && 'disabled',
])

const toggleIcon: ComputedRef<IconName> = computed(() =>
  modelValue.value ? 'checkmark' : 'delete',
)

defineOptions({
  inheritAttrs: false,
})
</script>

<template>
  <div class="form-switch" :class="{ 'cursor-not-allowed': disabled }">
    <Flex align-items="center" column-gap="0.5rem" class="switch-container">
      <!-- TODO: move this loader into <Control> Component and remove flex -->
      <FlexItem v-if="isLoading">
        <Transition name="fade-fast" mode="out-in">
          <Spinner class="switch-spinner" size="sm" />
        </Transition>
      </FlexItem>

      <FlexItem>
        <div class="switch" tabindex="0" :class="appearance">
          <input
            :checked="modelValue"
            type="checkbox"
            v-bind="$attrs"
            :disabled="disabled"
            @click="emit('click', ($event.target as HTMLInputElement)?.checked)"
            @change="
              emit('update:modelValue', ($event.target as HTMLInputElement)?.checked)
            "
          />
          <Icon v-if="hasIcon" :name="toggleIcon" size="sm" />
          <span
            class="toggler"
            @click="modelValue = !modelValue"
            @keyup.enter="modelValue = !modelValue"
          />
        </div>
      </FlexItem>
    </Flex>
  </div>
</template>
